import { useConfigStore } from '@/stores';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useOrientation, useTickSoundInteraction } from '@/hooks';
import { useHasMultipleBuildings } from '@/hooks/poi/useShopData';
import type { PoiContentSlide } from '@/types/shop';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import { memo, useCallback } from 'react';
import { AppUtils } from '@/utils/app';

interface ShopCarouselItemProps {
    content: PoiContentSlide;
    tabIndex: number;
    onClick?: (data: PoiContentSlide) => void;
}

const ShopCarouselItem = memo(({ content, tabIndex, onClick }: ShopCarouselItemProps) => {
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const hasMultipleBuildings = useHasMultipleBuildings();
    const { t } = useTranslation();
    const { isHorizontal } = useOrientation();
    const { handleInteraction, handleKeyboardInteraction } = useTickSoundInteraction();

    const handleClick = useCallback((e: any) => {
        const parent = e.currentTarget as HTMLElement;
        parent.classList.add('poi-active');
        handleInteraction(e, () => onClick?.(content))
    }, [handleInteraction, onClick, content]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => handleKeyboardInteraction(e, () => onClick?.(content)),
        [handleKeyboardInteraction, onClick, content]
    );

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            key={content.id}
            tabIndex={tabIndex}
            data-interactive
            data-playsound='ting'
            data-role={t('common.button.tts.popup_button')}
            className={clsx(styles.wrapper, {
                [styles['wrapper__contrast']]: contrast,
                [styles['wrapper__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
            })}
            aria-label={`${content.name}, ${content.keyword ? t('shop.slide.hashtag', { hashtag: content.keyword.replaceAll('#', '') }) + ', ' : ''} ${hasMultipleBuildings ? content.buildingName + ', ' : ''} ${t('shop.search.tts.content.floor', { floor: AppUtils.getFloorTTSText(content.floor) })}`}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            {/* Poi Image */}
            <div
                className={clsx(styles['wrapper__poi--image'], {
                    [styles['wrapper__poi--image__contrast']]: contrast,
                    [styles['wrapper__poi--image__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
                })}
            >
                {content.url ? <img src={content.url} alt={`${content.name} logo`} loading="lazy" data-role={t('common.data_role.image')} /> : <PlaceHolder />}
            </div>

            {/* Poi Info */}
            <div
                className={clsx(styles['wrapper__poi--info'], {
                    [styles['wrapper__poi--info__contrast']]: contrast,
                    [styles['wrapper__poi--info__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
                })}
            >
                <div
                    className={clsx(styles['wrapper__poi--info--name'], {
                        [styles['wrapper__poi--info--name__contrast']]: contrast,
                        [styles['wrapper__poi--info--name__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
                    })}
                >
                    <h3
                        className={clsx(styles['wrapper__poi--info--name__text'], {
                            [styles['wrapper__poi--info--name__text--clamp-2']]: !content?.keyword,
                        })}
                    >
                        {content.name}
                    </h3>
                    {content.keyword && (
                        <div className={styles['wrapper__poi--info--name--keyword']}>
                            <p>{content.keyword}</p>
                        </div>
                    )}
                </div>
                <div className={clsx(styles['wrapper__poi--info--floor'], { [styles['wrapper__poi--info--floor__contrast']]: contrast })}>
                    {hasMultipleBuildings && (
                        <span className={styles['wrapper__poi--info--floor--building-name']}>{content.buildingName}</span>
                    )}
                    {hasMultipleBuildings && (
                        <div
                            className={clsx(styles['wrapper__poi--info--floor--divider'], {
                                [styles['wrapper__poi--info--floor--divider__contrast']]: contrast,
                            })}
                        />
                    )}
                    <span className={clsx(styles['wrapper__poi--info--floor--floor-number'], 'number')}>{content.floor}</span>
                </div>
            </div>
        </div>
    );
});

ShopCarouselItem.displayName = 'ShopCarouselItem';

export default ShopCarouselItem;
